import { CanvasTextMetrics, TextStyle, type TextStyleOptions } from 'pixi.js';

/**
 * The narrowest wrap width that keeps `text` on as few lines as `max`
 * allows, so a wrapped line shares its words evenly between the lines
 * instead of leaving one word alone on the last ("one landmark at a /
 * time.").
 */
export function balancedWrapWidth(text: string, style: TextStyleOptions, max: number): number {
  const lines = (width: number) => lineCount(text, style, width);
  const fewest = lines(max);
  if (fewest < 2) return max;
  let lo = max / 2;
  let hi = max;
  while (hi - lo > 2) {
    const mid = (lo + hi) / 2;
    if (lines(mid) <= fewest) hi = mid;
    else lo = mid;
  }
  return Math.ceil(hi);
}

/**
 * `text` set for a box `max` wide: when it wraps and is made of sentences
 * that each fit on a line, one sentence to a line ("No bombs this time." /
 * "Tap STOP when you like."), as long as that takes no more lines; otherwise
 * `text` as it is. Pair it with {@link balancedWrapWidth}.
 */
export function sentenceLines(text: string, style: TextStyleOptions, max: number): string {
  const wrapped = lineCount(text, style, max);
  if (wrapped < 2) return text;
  const sentences = text.split(/(?<=[.!?\u2026])\s+/);
  if (sentences.length < 2 || sentences.length > wrapped) return text;
  return sentences.every((s) => lineCount(s, style, max) === 1) ? sentences.join('\n') : text;
}

function lineCount(text: string, style: TextStyleOptions, width: number): number {
  return CanvasTextMetrics.measureText(text, new TextStyle({ ...style, wordWrap: true, wordWrapWidth: width })).lines.length;
}
